import { createContext, useContext, useState, useCallback, useRef } from 'react';
import API from './api';
import { toast } from 'sonner';

const EditContext = createContext(null);

export function EditProvider({ children }) {
  const [editMode, setEditMode] = useState(false);
  const [changes, setChanges] = useState({}); // { pageKey: { fieldKey: value }, __nav__: { header_items: [...], footer_links: [...] } }
  const [saving, setSaving] = useState(false);
  const changesRef = useRef(changes);
  changesRef.current = changes;

  const hasChanges = Object.keys(changes).some(k => {
    if (k === '__nav__') return Object.keys(changes[k] || {}).length > 0;
    return Object.keys(changes[k]).length > 0;
  });

  const updateField = useCallback((pageKey, fieldKey, value) => {
    setChanges(prev => ({
      ...prev,
      [pageKey]: { ...(prev[pageKey] || {}), [fieldKey]: value },
    }));
  }, []);

  const updateNav = useCallback((section, items) => {
    setChanges(prev => ({
      ...prev,
      __nav__: { ...(prev.__nav__ || {}), [section]: items },
    }));
  }, []);

  const saveAll = useCallback(async () => {
    const pending = changesRef.current;
    const pageKeys = Object.keys(pending).filter(k => k !== '__nav__' && Object.keys(pending[k]).length > 0);
    const navPending = pending.__nav__ || {};
    const hasNavChanges = Object.keys(navPending).length > 0;

    if (pageKeys.length === 0 && !hasNavChanges) return;
    setSaving(true);
    try {
      for (const pageKey of pageKeys) {
        const { data: existing } = await API.get(`/pages/${pageKey}`).catch(() => ({ data: {} }));
        const merged = { ...existing, ...pending[pageKey] };
        await API.put(`/admin/pages/${pageKey}`, merged);
      }
      if (hasNavChanges) {
        const { data: currentNav } = await API.get('/nav').catch(() => ({ data: { navigation: {} } }));
        const currentNavSettings = currentNav.navigation || {};
        const merged = { ...currentNavSettings, ...navPending };
        await API.put('/admin/settings/navigation', merged);
      }
      setChanges({});
      toast.success('All changes saved');
    } catch (e) {
      toast.error('Failed to save changes');
    }
    setSaving(false);
  }, []);

  const discardAll = useCallback(() => {
    setChanges({});
    window.location.reload();
  }, []);

  const toggleEdit = useCallback(() => {
    setEditMode(prev => {
      if (prev && Object.keys(changesRef.current).some(k => {
        if (k === '__nav__') return Object.keys(changesRef.current[k] || {}).length > 0;
        return Object.keys(changesRef.current[k]).length > 0;
      })) {
        if (!window.confirm('You have unsaved changes. Discard them?')) return prev;
        setChanges({});
      }
      return !prev;
    });
  }, []);

  return (
    <EditContext.Provider value={{ editMode, toggleEdit, updateField, updateNav, saveAll, discardAll, hasChanges, saving, changes }}>
      {children}
    </EditContext.Provider>
  );
}

export function useEdit() {
  return useContext(EditContext) || { editMode: false };
}
